import { ILockService } from './lock-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IUserPriviledgeService, Scope } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { reaction, transaction } from 'mobx';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import ColorPicker from '@com-pc/color-picker';
import { ITextEditorStateService } from '@src/slide/application/service/text-editor-state-service/text-editor-state-service.interface';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { isPageInsideChangeMutation } from '@tencent/slide-sdk-model';
import get from 'lodash/get';
import { EditEvents } from '@src/slide/domain/slide-app/core/interfaces/event.interface';
import { CommandData } from '@tencent/slide-sdk-model/src/app/command-data';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';

export class LockService implements ILockService {
    private selectionService?: ISelectionService;
    private userPriviledgeService: IUserPriviledgeService;
    private rightSideBarManager?: IRightSideBarManager;
    private menuManager?: IMenuManager;
    private editingStateManager?: EditingStateManager;
    private textEditorStateService?: ITextEditorStateService;
    private editorInstanceService?: IEditorInstanceService;
    private historyManager?: IHistoryManager;
    private preloadAction: (() => void) | null = null;

    public setSelectionServiceDelegate = (selectionService: ISelectionService) => {
        if (!this.selectionService) {
            this.selectionService = selectionService;
        }
    };

    public setUserPriviledgeServiceDelegate = (userPriviledgeService: IUserPriviledgeService) => {
        if (!this.userPriviledgeService) {
            (window as any).userPriviledgeService = userPriviledgeService;
            this.userPriviledgeService = userPriviledgeService;
        }
    };

    public setRightSideBarManagerDelegate = (rightSideBarManager: IRightSideBarManager) => {
        if (!this.rightSideBarManager) {
            this.rightSideBarManager = rightSideBarManager;
        }
    };

    public setMenuManagerDelegate = (menuManager: IMenuManager) => {
        if (!this.menuManager) {
            this.menuManager = menuManager;
        }
    };

    public setEditingStateManagerDelegate = (editingStateManager: EditingStateManager) => {
        if (!this.editingStateManager) {
            this.editingStateManager = editingStateManager;
        }
    };

    public setTextEditorStateService = (textEditorStateService: ITextEditorStateService) => {
        if (!this.textEditorStateService) {
            this.textEditorStateService = textEditorStateService;
        }
    };

    public setEditorInstanceService = (editorInstanceService: IEditorInstanceService) => {
        if (!this.editorInstanceService) {
            this.editorInstanceService = editorInstanceService;
        }
    };

    public setHistoryManagerDelegate(historyManager: IHistoryManager) {
        if (!this.historyManager) {
            this.historyManager = historyManager;
        }
    }

    public initEvents = () => {
        this.registerLockEvent();
        this.registerUnlockEvent();
        this.registerQueueEvent();
        this.registerEditorContentChangeFlagReaction();
    };

    public checkLock = () => {
        const currentSlideIndex = this.selectionService?.currentSlideIndex;
        // 切换页的时候解锁
        const myLock = this.lockManager?.myLockInfo;
        if (myLock && myLock.slide.index !== currentSlideIndex) {
            this.lockManager?.unlockById(myLock.slide.id);
        }
        const lock = currentSlideIndex !== undefined && this.lockManager
            ?.getLockInfoByIndex(currentSlideIndex, this.selectionService?.currentSlideType || UIModelType.SLIDE);

        if ((lock && !lock.user.isSelf) || this.userPriviledgeService.isOverCoorperateNum) {
            // 如果是锁定的，或者触发了最高协作人数限制
            this.lockAction();
        } else if (this.isPreload()) {
            // 如果是选中的页中存在未加载完的
            this.lockAction();
            // 加载完，若还是当前状态，且没有其他干预，则解除锁定
            this.preloadRecover();
        } else {
            this.makeEditable();
        }
    };

    private get lockManager() {
        return OnlineEditServices.CooperationLockManager();
    }

    private lockAction() {
        // preload 进入只读状态，协同锁可以复制
        this.setCopyable();
        // 关闭右侧面板
        this.rightSideBarManager?.setIsPropertyEditorShown(false);
        this.rightSideBarManager?.setIsAnimationEditorShown(false);
        this.rightSideBarManager?.setIsSelectWindowShown(false);
        this.rightSideBarManager?.setIsSlideRatingShown(false);
        // 隐藏所有菜单
        this.hideAllMenu();
        // 退出裁剪态
        this.updateElementCropState(false);
        // 退出编辑态
        this.editorInstanceService?.getEditorInstance()?.triggerBlur();
    }

    /**
     * 如果是因为sdk尚未加载完毕造成的锁页，等slide加载完成后需要解开锁页
     * 注意：过程中如果有其他因素干预了锁页，则说明此操作已过时，新的锁页状态已经由心的操作决定，当前reaction作废
     * @private
     */
    private preloadRecover() {
        if (!this.isPreload()) {
            return;
        }
        const lockSlideId = this.selectionService?.currentSlide?.id;
        this.preloadAction = reaction(
            () => this.isPreload(),
            () => {
                if (!this.isPreload()) {
                    this.preloadAction?.();
                    this.preloadAction = null;
                    const curSlideId = this.selectionService?.currentSlide?.id;
                    if (curSlideId && (curSlideId === lockSlideId)) {
                        this.makeEditable();
                    }
                }
            },
        );
    }

    /**
     * 协同锁加锁
     */
    private registerLockEvent = () => {
        const lockCb = (lockInfo) => {
            if (this.historyManager?.isShowingHistory) {
                return;
            }
            const currentSlide = this.selectionService?.currentSlide;
            // 保护，可能没有当前slide！
            if (!currentSlide) {
                return;
            }
            const lock = lockInfo.find(lock => lock.slide.id === currentSlide.id);
            // 如果其他人锁定了该页
            if (lock && !lock.user.isSelf) {
                // 进入只读状态
                this.setCopyable();
                // 关闭右侧面板
                this.rightSideBarManager?.setIsPropertyEditorShown(false);
                this.rightSideBarManager?.setIsAnimationEditorShown(false);
                this.rightSideBarManager?.setIsSelectWindowShown(false);
                this.rightSideBarManager?.setIsSlideRatingShown(false);
                // 关闭所有菜单
                this.hideAllMenu();
                // 退出编辑态
                this.editorInstanceService?.getEditorInstance()?.triggerBlur();
                if (ColorPicker.isShown()) {
                    ColorPicker.hide();
                }
            }
        };
        SlideDataServices.EventManager.promise().then((event) => {
            event.on(EditEvents.Lock, lockCb);
            event.on(EditEvents.SetDocCopyable, this.setCopyable);
            event.on(EditEvents.SetDocEditable, this.setEditable);
        });
    };

    /**
     * 协同锁解锁
     */
    private registerUnlockEvent = () => {
        const unlockCb = (unlockInfo) => {
            if (this.historyManager?.isShowingHistory) {
                return;
            }
            const currentSlide = this.selectionService?.currentSlide;
            // 保护，可能没有当前slide！
            if (!currentSlide) {
                return;
            }
            const unlock = unlockInfo.find(unlock => unlock.slide.id === currentSlide.id);
            if (unlock) {
                this.makeEditable();
            }
        };
        SlideDataServices.EventManager.promise().then((event) => {
            event.on(EditEvents.Unlock, unlockCb);
        });
    };

    /**
     * 提交cs时加锁
     */
    private registerQueueEvent = () => (async () => {
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService
            .promise();
        const sdkEventManager = await SlideDataServices.SDKDataServices.EventManager.promise();
        const lockManager = await OnlineEditServices.CooperationLockManager.promise();
        sdkEventManager.revisionEvents.onEnqueue((userChange) => {
            let pageIndex = -1;
            userChange.commands.map(({ mutations }) => mutations ?? []).flat()
                .find((mutationsData) => {
                    const [mutationData] = CommandData.fromMutationData([mutationsData]).getValidMutationData();
                    if (isPageInsideChangeMutation(mutationData)) {
                        const pageId = get(mutationData, 'pageId');
                        pageIndex = presentationStoreService.getPresentation().getPageIndexById(pageId);
                    }
                    return pageIndex !== -1;
                });
            if (pageIndex !== -1 && pageIndex === this.selectionService?.currentSlideIndex) {
                // 如果没有锁，则申请加锁
                const lock = lockManager.getLockInfoByIndex(pageIndex, this.selectionService.currentSlideType);
                if (!lock) {
                    lockManager.lockByIndex(pageIndex, this.selectionService.currentSlideType);
                }
                lockManager.unlockMyLockInFuture();
            }
        });
    })();

    /**
     * 当输入框产生第一次文字变化时，加锁
     */
    private registerEditorContentChangeFlagReaction = () => {
        reaction(() => this.textEditorStateService?.editorContentChangeFlag, () => {
            // 如果没有锁，则申请加锁
            const index = this.selectionService?.currentSlideIndex;
            if (index !== undefined) {
                const lock = this.lockManager
                    ?.getLockInfoByIndex(index, this.selectionService?.currentSlideType || UIModelType.SLIDE);
                if (!lock) {
                    this.lockManager
                        ?.lockByIndex(index, this.selectionService?.currentSlideType || UIModelType.SLIDE);
                }
            }
            this.lockManager?.unlockMyLockInFuture();
        });
    };
    private hideAllMenu = () => {
        if (this.menuManager) {
            this.menuManager.hideAllMenu();
        }
    };

    private updateElementCropState = (isCropping) => {
        transaction(() => {
            if (this.editingStateManager) {
                this.editingStateManager.isCroping = isCropping;
            }
        });
    };

    /**
     * 赋予编辑权限
     * @private
     */
    private makeEditable() {
        SlideDataServices.PrivilegeManager.promise().then(({ editable }) => {
            if (!editable) {
                return;
            }

            this.setEditable();
        });
    }

    /**
     * 当前选择的页中存在未加载完成的
     */
    private isPreload = () => this.selectionService?.selectedSlides.selectedArray.some(e => e.preload)
            || this.selectionService?.currentSlide?.preload;

    private setCopyable = () => {
        this.userPriviledgeService.setPPTEditorState({
            state: this.isPreload() ? PPTEditorStateType.SELECTABLE : PPTEditorStateType.COPYABLE,
            scope: Scope.CURRENT,
        });
        // 已有最新的影响因素，清掉过时的
        this.preloadAction?.();
    };

    private setEditable = () => {
        this.userPriviledgeService.setPPTEditorState({
            state: PPTEditorStateType.EDITABLE,
            scope: Scope.CURRENT,
        });
        // 已有最新的影响因素，清掉过时的
        this.preloadAction?.();
    };
}
